/**
 * The academy-admin payment-operations seam.
 *
 * ONE FILE DECIDES THE THREE-VALUED QUESTIONS, and no component is allowed to decide them.
 * That is the same rule `shared/lib/pricing.ts` states for prices, and it is here for the
 * same reason: from inside a component, "the server said no", "the server did not say" and
 * "the server said nothing applies" look identical, and they are opposite facts. In this
 * module the opposite fact is somebody's money.
 *
 * WHAT THE BACKEND OWNS AND THIS FILE MUST NOT RE-DERIVE
 * -----------------------------------------------------
 *   which payment method this was          `truth_banner.method`, NOT `order.payment_method_type`
 *   the refund window and whether it closed `truth_banner.window`
 *   whether a refund is possible at all     `refund_available` + `refund_unavailable_reason`
 *   what a revocation would revoke or keep  `grant_decisions`
 *   the smallest refund Stripe will accept  `minimum_refund_minor`
 *   whether the operator may write here     `controls`
 *
 * Every one of those is computed on the server and sent. A TypeScript copy of any of them
 * would be a second copy of a rule that changes — the argument `PaymentsConfig.error_sentence`
 * already makes for operator copy — and the two copies would disagree in the direction nobody
 * tests.
 *
 * THE ONE RULE THIS FILE DOES HOLD A COPY OF is the reason's shape (ten characters, more than
 * one word). That is a duplicate on purpose: it is a form-validation message the operator
 * should see before submitting, not a safety net. The server refuses it too, and now refuses
 * it BEFORE the Stripe call — `openedx_thinlms_payment_ops.operations.refund` step 1. Until
 * that fix, a blank reason moved real money and then raised; if this file were the only check,
 * a curl would have found that.
 *
 * WHAT IS NEVER SAID HERE
 * -----------------------
 * The markup is "payment processing", never a platform fee, service fee or commission — the
 * standing wording law in `PRICE_COPY`. The platform takes no commission, holds none of the
 * funds and cannot fund a refund: every sen is the academy's, a refund is a debit against the
 * academy's own Stripe balance, and there is no platform-funded fallback to offer. A shortfall
 * is stated as a shortfall with the number, and the next action names what the ACADEMY must do.
 */

import { openedx, ApiError } from '../../shared/lib/openedx';

const PAYMENT_OPS_API = '/api/payment-ops/v1';

/**
 * Stamped into `AdminPaymentAction.request_metadata.ui_version` on every write.
 *
 * The audit trail's only way to distinguish "an admin did this in the product" from "somebody
 * drove the API with a session cookie", which is exactly how the refund path was exercised
 * before these screens existed. Bump it when the operator-facing meaning of an action changes,
 * not on every edit — a version nobody can map to a behaviour is noise in a permanent row.
 */
export const OPS_UI_VERSION = 'payment-ops-ui/1';

const UI_HEADERS = { 'X-Thinlms-Ui-Version': OPS_UI_VERSION };

/* ------------------------------------------------------------------ query keys ---- */

/**
 * One root key, so a single invalidation reconciles the list, the order screen and the audit
 * page after a write. Same shape as `PRICING_SEAM_KEY`.
 */
export const PAYMENT_OPS_KEY = ['payment-ops'] as const;

/**
 * The delimiter, written as an escape.
 *
 * `join('')` made `['ab','c']` and `['a','bc']` the same cache entry, and a delimiter written
 * as a LITERAL control character made the file binary to git and unreviewable in a diff. Both
 * happened here already (`33d41e1`). A filter set is a list of operator-supplied strings, so
 * it has the same collision surface as a list of course ids.
 */
const KEY_DELIMITER = '\u0000';

export interface OrderListFilters {
  q?: string;
  paymentStates?: readonly string[];
  accessStates?: readonly string[];
  limit?: number;
  offset?: number;
}

export const orderListKey = (filters: OrderListFilters) =>
  [
    ...PAYMENT_OPS_KEY,
    'orders',
    [
      filters.q ?? '',
      [...(filters.paymentStates ?? [])].sort().join(','),
      [...(filters.accessStates ?? [])].sort().join(','),
      String(filters.limit ?? ''),
      String(filters.offset ?? ''),
    ].join(KEY_DELIMITER),
  ] as const;

export const orderDetailKey = (orderRef: string) =>
  [...PAYMENT_OPS_KEY, 'order', orderRef] as const;

export const auditKey = (orderRef: string) => [...PAYMENT_OPS_KEY, 'audit', orderRef] as const;

/* ----------------------------------------------------------------- wire types ---- */

/** Every amount below is an INTEGER IN MINOR UNITS. RM 85.00 is 8500. No floats, ever. */

export interface WireLearner {
  username: string;
  email: string;
}

export interface WireOrderItem {
  kind: string;
  key: string;
}

export interface WireOrderSummary {
  order_ref: string;
  tenant_slug: string;
  learner: WireLearner;
  item: WireOrderItem;
  base_minor: number;
  fee_minor: number;
  /** `base + fee`. THE QUOTE. Refunding this over-refunds — see `refundBasis`. */
  quoted_total_minor: number;
  /** NULLABLE: null means no payment ever settled. Not zero. */
  charged_minor: number | null;
  charge_currency: string;
  payment_state: string;
  access_state: string;
  payment_method_type: string;
  paid_at: string | null;
  version: string;
  refundable_minor: number;
  created_at: string | null;
}

export interface WireOrderDetail extends WireOrderSummary {
  stripe_charge_id: string;
  stripe_payment_intent_id: string;
  stripe_account_id: string;
}

export interface WireRefundRemainder {
  /** 0-coerced here, unlike `order.charged_minor`. Do not cross-read the two. */
  charged_minor: number;
  refunded_minor: number;
  pending_minor: number;
  refundable_minor: number;
  currency: string;
}

export interface WireWindow {
  bounded: boolean;
  expired: boolean;
  deadline: string | null;
  days_left: number | null;
  /** PRESENT ONLY when `bounded` is true. `undefined` on a card order. */
  urgent?: boolean;
}

export interface WireTruthBanner {
  method: string;
  label: string;
  tone: 'warning' | 'info';
  lines: string[];
  window: WireWindow;
  partial_refunds_supported: boolean;
  disputable: boolean;
  source: string;
}

export interface WireNetPosition {
  charged_minor: number;
  /** An ESTIMATE (3% + RM1.00), not a measured `balance_transaction.fee`. Labelled as one. */
  stripe_fee_retained_minor: number;
  received_minor: number;
  /** The FULL remainder, not what the operator typed. Use `netAt()` for a typed amount. */
  refund_minor: number;
  academy_net_minor: number;
  borne_by: string;
}

export interface WireExFeePreset {
  amount_minor: number;
  label: string;
  explanation: string;
  keeps_minor: number;
}

export interface WireStripeReason {
  value: string;
  label: string;
  /** Present only on `fraudulent`, which changes Stripe Radar behaviour. */
  consequence?: string;
}

export type GrantDecision =
  | 'revoked'
  | 'superseded_by_other_grant'
  | 'retained_pre_existing'
  | string;

export interface WireGrantDecision {
  target_kind: string;
  target_key: string;
  granted_via: string;
  parent_target_key: string;
  decision: GrantDecision;
  note: string;
  granting_tenant_key: string;
  owning_org: string;
  is_licensed: boolean;
}

/** The OFFER shape, on the order screen. The RESULT shape after a write is different. */
export interface WireCertificateOffer {
  kind: string;
  identifier: string;
  status: string;
  issued_at: string | null;
  verify_uuid: string;
  already_revoked: boolean;
}

export interface WireCertificateResult {
  kind: string;
  identifier: string;
  decision: 'retained' | 'revoked' | 'already_revoked' | 'nothing_to_revoke' | string;
  verify_uuid?: string;
  mechanism?: string;
}

export interface WireAuditEntry {
  operation_id: string;
  /**
   * The order this happened to. `''` for the actions that have no order behind them — a
   * free-enrolment cancellation is the real one — never null, so there is one spelling of absent.
   */
  order_ref: string;
  action: string;
  actor_username: string;
  actor_is_superadmin: boolean;
  /** `null`, not `''`, in the timeline. `''` in the CSV export. */
  acted_under_mandate: string | null;
  occurred_at: string;
  reason: string;
  /** `null` on a no-money action; `no_money_moved` is the affirmative marker. */
  amount_minor: number | null;
  currency: string;
  no_money_moved: boolean;
  outcome: string;
  outcome_detail: string;
  payment_state_after: string;
  access_state_after: string;
  stripe_object_ids: Record<string, string>;
  affected_grants: WireGrantDecision[];
  certificate_decisions: WireCertificateResult[];
  settled_out_of_band: boolean;
  under_mandate: boolean;
  settled_at: string | null;
}

export interface WireObligation {
  id: string;
  amount_minor: number;
  currency: string;
  state: 'open' | 'in_progress' | 'settled_out_of_band' | 'written_off' | string;
  /** `''` is normal and means unassigned — never render it as a name. */
  assignee: string;
  failure_reason: string;
  opened_at: string;
}

/**
 * `operations` is ABSENT, never present-and-false, when the caller may not write.
 *
 * There is no `enabled: false` to flip in dev tools because the key is not in the response.
 * Branch on `'operations' in controls`, never on a per-verb boolean.
 */
export interface WireControls {
  read_only: boolean;
  reason_code?: 'write_not_enabled' | 'mandate_required' | 'no_write_authority' | string;
  read_only_reason?: string;
  operations?: string[];
}

export interface WireNeedsAction {
  open_obligations: number;
  open_obligation_total_minor: number;
  open_disputes: number;
  /** `-1` MEANS UNKNOWN — the order table could not be read. Never render it as a count. */
  blocked_upstream_orders: number;
  revoke_failed_orders: number;
  refund_pending_past_window: number;
}

export interface WireOrderScreen {
  order: WireOrderDetail;
  refund: WireRefundRemainder;
  truth_banner: WireTruthBanner;
  refund_available: boolean;
  refund_unavailable_reason: string;
  net_position: WireNetPosition;
  fee_is_not_returned: string;
  ex_fee_preset: WireExFeePreset;
  minimum_refund_minor: number;
  stripe_reasons: WireStripeReason[];
  stripe_reason_default: string;
  revoke_access_default: boolean;
  partial_refund_note: string;
  grant_decisions: WireGrantDecision[];
  certificates: WireCertificateOffer[];
  certificate_choice_required: boolean;
  regrant_side_effect: string;
  audit: WireAuditEntry[];
  obligations: WireObligation[];
  version: string;
  controls: WireControls;
  needs_action: WireNeedsAction;
}

export interface WireOrderList {
  orders: WireOrderSummary[];
  total: number;
  limit: number;
  offset: number;
  controls: WireControls;
}

export interface WireBalance {
  state: 'sufficient' | 'short' | 'unknown' | string;
  blocked: boolean;
  warning: {
    header: string;
    body: string;
    shortfall_minor: number;
    blocked: boolean;
    mereka_can_fund: boolean;
  } | null;
  note?: string;
  /** ABSENT when the balance could not be read. Absent, not zero — zero is a real balance. */
  available_minor?: number;
}

export interface WireRefundResult {
  operation: 'refund' | 'refund_keep_access' | string;
  operation_id: string;
  order_ref: string;
  amount_minor: number;
  currency: string;
  stripe_refund_id: string;
  state_update_mode: string;
  /** ALWAYS `"requested"`. Never render this as "refunded" — see `OPS_COPY.requested`. */
  status: string;
  message: string;
  payment_state: string;
  access_state: string;
  grant_decisions: WireGrantDecision[];
  certificates: WireCertificateResult[];
  balance_warning: WireBalance['warning'];
  balance: WireBalance;
  audit_action_id: string;
}

export interface WireAccessResult {
  operation: string;
  operation_id: string;
  order_ref: string;
  payment_state?: string;
  access_state: string;
  money_moved: boolean;
  grant_decisions?: WireGrantDecision[];
  restored?: WireGrantDecision[];
  certificates?: WireCertificateResult[];
  note?: string;
  audit_action_id: string;
}

export interface WireFreeDecision {
  target_kind: string;
  target_key: string;
  verdict:
    | 'revoke'
    | 'retain_independently_owned'
    | 'retain_unproven'
    | 'retain_pre_existing'
    | 'not_held'
    | string;
  basis: 'recorded_at_grant' | 'inferred_temporal' | 'unknown' | string;
  note: string;
  evidence: Record<string, unknown>;
  proven: boolean;
  needs_confirmation: boolean;
}

export interface WireFreePreview {
  pathway_key: string;
  learner: WireLearner;
  decisions: WireFreeDecision[];
  requires_confirmation: string[];
  will_revoke_automatically: string[];
  /** TWO keys here. The cancel response's `retained[]` has three. Not the same type. */
  retained: { target_key: string; reason: string }[];
  certificates: WireCertificateOffer[];
  certificate_choice_required: boolean;
  money: { no_payment_taken: boolean; explanation: string };
  regrant_side_effect: string;
}

export interface WireFreeCancelResult {
  pathway_key: string;
  learner: WireLearner;
  operation_id: string;
  revoked: string[];
  retained: { target_key: string; decision: string; reason: string }[];
  /** Offered but not ticked, so LEFT IN PLACE. Non-empty means partially succeeded. */
  not_confirmed: string[];
  certificates: WireCertificateResult[];
  audit_action_id: string;
}

export interface WireAuditList {
  entries: WireAuditEntry[];
  needs_action: WireNeedsAction;
  controls: WireControls;
}

/* --------------------------------------------------- the three-valued readers ---- */

/**
 * May this operator write here, and if not, whose problem is it?
 *
 * `kind: 'writable'` is the ONLY state in which a screen may render a refund or revoke
 * control at all. The other three are not "disabled" — the affordance does not exist, because
 * the server did not send it. R8.3.2 / AC-266.
 *
 * The three refusals have three different next actions, which is why they are three states
 * and not one boolean. Collapsing them sends people to the wrong person: a superadmin told
 * "this academy is not enabled" goes to the platform, when what they need is the academy's
 * own authorisation — and vice versa.
 */
export type WriteGate =
  | { kind: 'writable'; operations: Set<string> }
  | { kind: 'not_enabled_here'; reason: string }
  | { kind: 'mandate_required'; reason: string }
  | { kind: 'no_authority'; reason: string }
  | { kind: 'unknown'; reason: string };

const GATE_FALLBACK =
  'This academy’s payment operations are read-only for you. Nothing here can move money.';

export function writeGate(controls: WireControls | null | undefined): WriteGate {
  // ABSENT PAYLOAD IS NOT PERMISSION. A screen that has not loaded its controls must render
  // no write affordances, exactly like one that loaded a refusal.
  if (!controls) return { kind: 'unknown', reason: GATE_FALLBACK };

  if (Array.isArray(controls.operations)) {
    return { kind: 'writable', operations: new Set(controls.operations) };
  }
  const reason = controls.read_only_reason || GATE_FALLBACK;
  if (controls.reason_code === 'write_not_enabled') return { kind: 'not_enabled_here', reason };
  if (controls.reason_code === 'mandate_required') return { kind: 'mandate_required', reason };
  if (controls.reason_code === 'no_write_authority') return { kind: 'no_authority', reason };
  // A refusal we do not have a code for is still a refusal. Read-only wins.
  return { kind: 'unknown', reason };
}

/** Is this specific verb offered? False whenever the gate is anything but `writable`. */
export function may(gate: WriteGate, operation: string): boolean {
  return gate.kind === 'writable' && gate.operations.has(operation);
}

/**
 * A count that may be unknown.
 *
 * `needs_action.blocked_upstream_orders` and `revoke_failed_orders` are `-1` when the order
 * table could not be read. Rendering `-1` is nonsense; coercing it to `0` is the "404 resolved
 * to free" bug in a new costume — it reports "nothing is stuck" on the evidence that we could
 * not look.
 */
export type CountState = { known: true; value: number } | { known: false };

export function countOf(raw: number | null | undefined): CountState {
  if (typeof raw !== 'number' || Number.isNaN(raw) || raw < 0) return { known: false };
  return { known: true, value: raw };
}

/**
 * What the refund window means for the control, and it is deliberately not a boolean.
 *
 * `open` and `unbounded` both allow a refund but say different things to the operator; a card
 * order has no deadline at all, and printing "0 days left" for it would be a lie. `urgent` is
 * ABSENT from the payload when the window is unbounded, so it is read as `=== true` rather
 * than for truthiness.
 *
 * A CLOSED WINDOW DISABLES THE CONTROL. It does not submit and let Stripe manufacture the
 * failure — the deadline is knowable before the click, and a refusal from Stripe arrives in
 * Stripe's words after the operator has committed.
 */
export type WindowState =
  | { kind: 'unbounded' }
  | { kind: 'open'; deadline: string; daysLeft: number | null; urgent: boolean }
  | { kind: 'closed'; deadline: string | null };

export function windowState(banner: WireTruthBanner | null | undefined): WindowState {
  const w = banner?.window;
  if (!w || !w.bounded) return { kind: 'unbounded' };
  if (w.expired) return { kind: 'closed', deadline: w.deadline ?? null };
  return {
    kind: 'open',
    deadline: w.deadline ?? '',
    daysLeft: typeof w.days_left === 'number' ? w.days_left : null,
    urgent: w.urgent === true,
  };
}

/* ------------------------------------------------------------- the arithmetic ---- */

/**
 * The basis, and the two numbers that are NOT it.
 *
 * `quoted_total_minor` is what the order row was created with and is the mistake a naive
 * implementation makes: the R5.4 worked example is list RM 100.00 + RM 5.00 processing =
 * RM 105.00 quoted, a 20% code applied at Stripe, RM 85.00 actually charged. Refunding the
 * quote over-refunds by RM 20.00 of the academy's own money.
 *
 * `charged_minor` is null when nothing ever settled, and null is not zero: `state: 'unsettled'`
 * says so rather than rendering RM 0.00, which reads as a free order.
 */
export type RefundBasis =
  | { state: 'unsettled' }
  | {
      state: 'settled';
      /** What the learner was actually charged, after any Stripe promotion code. */
      chargedMinor: number;
      /** The processing amount baked into the price. Never called a fee of the platform's. */
      processingMinor: number;
      /** The author's price. */
      listMinor: number;
      /** `quoted - charged`, i.e. what a Stripe promotion code took off. 0 when none did. */
      discountMinor: number;
      alreadyRefundedMinor: number;
      pendingMinor: number;
      /** The most this refund may be. */
      refundableMinor: number;
      currency: string;
    };

export function refundBasis(screen: WireOrderScreen): RefundBasis {
  const { order, refund } = screen;
  if (order.charged_minor === null || order.charged_minor === undefined) {
    return { state: 'unsettled' };
  }
  const charged = order.charged_minor;
  return {
    state: 'settled',
    chargedMinor: charged,
    processingMinor: order.fee_minor,
    listMinor: order.base_minor,
    // Derived, because no field carries it: the promotions app's redemption model is declared
    // in settings and read by nothing, so the discount is only visible as the gap between the
    // quote and the charge. Clamped at 0 — a charge ABOVE the quote is not a negative
    // discount, it is a disagreement, and `net_position` is where that shows up.
    discountMinor: Math.max(0, order.quoted_total_minor - charged),
    alreadyRefundedMinor: refund.refunded_minor,
    pendingMinor: refund.pending_minor,
    refundableMinor: refund.refundable_minor,
    currency: refund.currency || order.charge_currency || 'MYR',
  };
}

/**
 * The academy's net position for the amount the operator actually typed.
 *
 * `net_position.refund_minor` from the server is hard-wired to the FULL remainder, because the
 * server computes it before an operator has typed anything. Showing that number next to a
 * partial amount box would show the arithmetic of a refund nobody asked for.
 *
 * The retained Stripe fee is the server's estimate, carried through unchanged rather than
 * recomputed here — one estimate, in one place, labelled.
 */
export interface NetAt {
  chargedMinor: number;
  feeRetainedMinor: number;
  refundMinor: number;
  academyNetMinor: number;
  feeIsEstimated: true;
}

export function netAt(screen: WireOrderScreen, refundMinor: number): NetAt {
  const net = screen.net_position;
  return {
    chargedMinor: net.charged_minor,
    feeRetainedMinor: net.stripe_fee_retained_minor,
    refundMinor,
    academyNetMinor: net.charged_minor - net.stripe_fee_retained_minor - refundMinor,
    feeIsEstimated: true,
  };
}

/* ------------------------------------------------------------- reason and ids ---- */

/**
 * The idempotency key for one whole operation, minted when the dialog OPENS and reused on
 * every retry of that dialog.
 *
 * A fresh id on retry defeats both the database constraint and Stripe's derived idempotency
 * key (`sha256("thinlms:refund:" + operation_id)`), which is how one refund becomes two. A
 * retry carrying the SAME id gets `409 operation_already_recorded`, and that is a success that
 * already happened — see `explainOpsFailure`.
 */
export function newOperationId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // No `Math.random()` fallback. An id that is not unique is worse than no id: the server
  // refuses a missing one, and silently trusts a colliding one.
  throw new Error(
    'This browser cannot generate a UUID, so no payment operation can be started safely from it.',
  );
}

export const REASON_MIN_LENGTH = 10;

/** Mirrors `audit.validate_reason`: ten characters, more than one word. See the file header. */
export function reasonProblem(reason: string): string | null {
  const cleaned = (reason ?? '').trim();
  if (!cleaned) return 'A reason is required.';
  if (cleaned.length < REASON_MIN_LENGTH || cleaned.split(/\s+/).length < 2) {
    return `At least ${REASON_MIN_LENGTH} characters and more than one word — “n/a” and “asked” are not reasons somebody can act on in three months.`;
  }
  return null;
}

/* --------------------------------------------------------------------- copy ---- */

/**
 * One place for the operator-facing sentences this UI adds, so they are greppable and
 * auditable — the same reason `PRICE_COPY` and `STATE_COPY` exist.
 *
 * Sentences the SERVER sends are NOT copied in here. `truth_banner.lines`,
 * `refund_unavailable_reason`, `read_only_reason`, `fee_is_not_returned`, `ex_fee_preset`
 * and every error `detail` are rendered verbatim: they carry measured numbers and real
 * deadlines, and a local paraphrase is how a screen ends up confidently wrong.
 */
export const OPS_COPY = {
  requested:
    'Requested. Stripe has not confirmed it yet, and this screen will not say “refunded” until it does.',
  moneyIsTheAcademys:
    'This refund is a debit against this academy’s own Stripe balance. The platform takes no commission on the sale and cannot fund a refund on the academy’s behalf.',
  processingNotReturned:
    'Stripe does not return its processing fee on the original payment, so a full refund costs this academy more than it received.',
  revokeDefault: 'Removing access is the ordinary meaning of a refund, so it is ticked by default.',
  partialStillRevokes:
    'A partial refund still removes all access to this purchase unless you untick the box.',
  retainedExplained:
    'These stay. The learner holds them independently of this purchase — bought separately, granted by another learning path, or already held before this order was fulfilled.',
  nothingRetained: 'Nothing here is held independently, so nothing will be kept back.',
  balanceUnknown:
    'We could not read this academy’s Stripe balance, so we do not know whether it covers this refund. That is not the same as knowing it does.',
  auditIsPermanent:
    'Every action on this screen writes a permanent row against your name, with your reason.',
  freeHasNoMoney:
    'No payment was taken for this enrolment, so there is nothing to refund. This only removes access.',
  confirmEachUnproven:
    'These cannot be proved to have come from this enrolment, so each one has to be named before it is removed. Anything you leave unticked stays.',
  unknownCount: 'Unknown — this could not be read',
} as const;

/* ------------------------------------------------------------------- errors ---- */

/**
 * The server's own sentence, always, plus a shape for the handful of codes that mean something
 * a raw sentence cannot convey.
 *
 * `outcome` matters as much as `message`: `already_done` is NOT a failure — the operation
 * happened, the screen is simply behind — and a retry button on it would invite a second
 * refund attempt against a constraint that will refuse it. `refusal` means nothing changed.
 * `partial` means money moved and something else did not.
 */
export interface OpsFailure {
  outcome: 'refusal' | 'already_done' | 'partial' | 'unknown';
  code: string;
  message: string;
  /** Set when the operator's next step is not "try again". */
  nextStep: string | null;
}

const NEXT_STEP: Record<string, string> = {
  balance_short_blocked:
    'Top up this academy’s Stripe balance, or wait for its next payout cycle, then try again. The learner has not been refunded and their access has not changed.',
  refund_window_expired:
    'Refund the learner directly — by transfer or by whatever means the academy uses — and record it here as settled outside Stripe.',
  refund_blocked_by_dispute:
    'Respond to the dispute in Stripe rather than refunding, or the learner is reimbursed twice. Removing access without refunding is still available below.',
  refund_request_failed:
    'An obligation has been opened so this is not forgotten. Settle it out of band, or write it off with a reason.',
  stale_order_version:
    'Somebody else changed this order after your screen was drawn. Reload it and look at the current state before acting.',
  write_not_enabled:
    'Refunds are not switched on for this academy on this deployment. Ask the platform operator to enable them.',
  not_enabled: 'Payment operations are not enabled on this deployment at all.',
  operation_already_recorded:
    'This exact operation was already recorded, so nothing has been done twice. Reload the order to see where it got to.',
  certificate_choice_required:
    'This learner holds a certificate covered by this action. Choose what happens to it — that is a decision, not a side effect.',
};

const ALREADY_DONE = new Set(['operation_already_recorded']);

export function explainOpsFailure(error: unknown): OpsFailure {
  if (!(error instanceof ApiError)) {
    return {
      outcome: 'unknown',
      code: 'unreachable',
      message:
        'The academy could not be reached, so we do not know whether anything happened. Reload this order before trying again.',
      nextStep: null,
    };
  }
  const body = error.body as { detail?: unknown; code?: unknown } | null;
  const code = typeof body?.code === 'string' ? body.code : '';
  const detail = typeof body?.detail === 'string' ? body.detail : '';

  // A 403 from the scope gate carries NO `code`, and a 404 is what "you may not write here"
  // looks like — deliberately indistinguishable from an unknown order, so that a guessed id
  // cannot confirm another academy's sale. Saying "not found" to an operator looking straight
  // at the order would be worse than saying nothing, so this names both possibilities.
  if (!code && (error.status === 403 || error.status === 401)) {
    return {
      outcome: 'refusal',
      code: 'forbidden',
      message: detail || 'You are not permitted to do this. Nothing has been changed.',
      nextStep: null,
    };
  }
  if (code === 'not_found') {
    return {
      outcome: 'refusal',
      code,
      message:
        detail ||
        'This academy has no such order — or you do not hold write authority over it. Those two answers are deliberately identical.',
      nextStep: null,
    };
  }

  return {
    outcome: ALREADY_DONE.has(code)
      ? 'already_done'
      : code === 'refund_request_failed'
        ? 'partial'
        : code
          ? 'refusal'
          : 'unknown',
    code: code || `http_${error.status}`,
    message:
      detail ||
      `The academy answered ${error.status} and said no more than that. Nothing here can be assumed to have happened.`,
    nextStep: NEXT_STEP[code] ?? null,
  };
}

/* ------------------------------------------------------------------- thunks ---- */

const qs = (filters: OrderListFilters): string => {
  const params = new URLSearchParams();
  if (filters.q?.trim()) params.set('q', filters.q.trim());
  for (const state of filters.paymentStates ?? []) params.append('payment_state', state);
  for (const state of filters.accessStates ?? []) params.append('access_state', state);
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.offset !== undefined) params.set('offset', String(filters.offset));
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const loadOrders = (filters: OrderListFilters) =>
  openedx.get<WireOrderList>(`${PAYMENT_OPS_API}/orders${qs(filters)}`);

export const loadOrderScreen = (orderRef: string) =>
  openedx.get<WireOrderScreen>(`${PAYMENT_OPS_API}/orders/${encodeURIComponent(orderRef)}`);

export const loadAudit = (orderRef = '') =>
  openedx.get<WireAuditList>(
    `${PAYMENT_OPS_API}/audit${orderRef ? `?order=${encodeURIComponent(orderRef)}` : ''}`,
  );

/**
 * The CSV export URL, as a plain href rather than a fetch.
 *
 * A GET needs no CSRF token and the browser already holds the session cookie, so a link is
 * both simpler and more honest than downloading a blob in JavaScript — the download is the
 * browser's, and a failure shows as a page rather than as a silently empty file.
 *
 * NOTE THE ASYMMETRY, which is the server's and not ours: the CSV mode ignores `order` and
 * honours `actor`/`action`/`outcome`/`since`/`until`, while the JSON mode does the opposite.
 * So this exports the WHOLE academy's trail, and the caller must say so next to the link.
 */
export const auditCsvUrl = (params: {
  actor?: string;
  action?: string;
  outcome?: string;
  since?: string;
  until?: string;
} = {}): string => {
  const query = new URLSearchParams({ format: 'csv' });
  for (const [key, value] of Object.entries(params)) if (value) query.set(key, value);
  return `${PAYMENT_OPS_API}/audit?${query.toString()}`;
};

export interface RefundRequest {
  operationId: string;
  amountMinor: number;
  reason: string;
  stripeReason?: string;
  revokeAccess: boolean;
  version: string;
  certificateDecisions?: Record<string, 'revoke' | 'retain'>;
}

export const requestRefund = (orderRef: string, body: RefundRequest) =>
  openedx.post<WireRefundResult>(
    `${PAYMENT_OPS_API}/orders/${encodeURIComponent(orderRef)}/refund`,
    {
      operation_id: body.operationId,
      amount_minor: body.amountMinor,
      reason: body.reason,
      stripe_reason: body.stripeReason ?? '',
      // A REAL BOOLEAN. The server does `bool(body.get("revoke_access", True))`, so the string
      // "false" is truthy and would revoke; omitting it revokes too.
      revoke_access: body.revokeAccess === true,
      version: body.version,
      // MUST be absent/empty when access is kept, or the server refuses with
      // `certificate_choice_unexpected`. Enforced here rather than trusted to each caller.
      certificate_decisions: body.revokeAccess ? (body.certificateDecisions ?? {}) : {},
    },
    UI_HEADERS,
  );

export interface AccessChangeRequest {
  operationId: string;
  reason: string;
  version: string;
  certificateDecisions?: Record<string, 'revoke' | 'retain'>;
}

export const cancelAccess = (orderRef: string, body: AccessChangeRequest) =>
  openedx.post<WireAccessResult>(
    `${PAYMENT_OPS_API}/orders/${encodeURIComponent(orderRef)}/cancel-access`,
    {
      operation_id: body.operationId,
      reason: body.reason,
      version: body.version,
      certificate_decisions: body.certificateDecisions ?? {},
    },
    UI_HEADERS,
  );

export const regrantAccess = (
  orderRef: string,
  body: { operationId: string; reason?: string; version: string },
) =>
  openedx.post<WireAccessResult>(
    `${PAYMENT_OPS_API}/orders/${encodeURIComponent(orderRef)}/regrant-access`,
    {
      operation_id: body.operationId,
      // OPTIONAL on this route alone, because re-granting restores access rather than removing
      // it. Sent when given, because a reason is still the useful thing in three months.
      reason: body.reason ?? '',
      version: body.version,
    },
    UI_HEADERS,
  );

export const previewFreeEnrolmentCancel = (body: {
  pathwayKey: string;
  username?: string;
  email?: string;
}) =>
  openedx.post<WireFreePreview>(
    `${PAYMENT_OPS_API}/free-enrolments/preview`,
    { pathway_key: body.pathwayKey, username: body.username ?? '', email: body.email ?? '' },
    UI_HEADERS,
  );

export const cancelFreeEnrolment = (body: {
  operationId: string;
  pathwayKey: string;
  reason: string;
  username?: string;
  email?: string;
  confirmRevokeKeys: readonly string[];
  certificateDecisions?: Record<string, 'revoke' | 'retain'>;
}) =>
  openedx.post<WireFreeCancelResult>(
    `${PAYMENT_OPS_API}/free-enrolments/cancel`,
    {
      operation_id: body.operationId,
      pathway_key: body.pathwayKey,
      reason: body.reason,
      username: body.username ?? '',
      email: body.email ?? '',
      confirm_revoke_keys: [...body.confirmRevokeKeys],
      certificate_decisions: body.certificateDecisions ?? {},
    },
    UI_HEADERS,
  );

export const settleObligation = (
  obligationId: string,
  body: { operationId: string; method: string; note: string },
) =>
  openedx.post<{ obligation_id: string; state: string; audit_action_id: string }>(
    `${PAYMENT_OPS_API}/obligations/${encodeURIComponent(obligationId)}/settle`,
    {
      operation_id: body.operationId,
      method: body.method,
      // `note`, not `reason`, on this branch — and the server checks ten characters here
      // WITHOUT the two-word rule. Different field, different rule, deliberately not merged.
      note: body.note,
    },
    UI_HEADERS,
  );

export const writeOffObligation = (
  obligationId: string,
  body: { operationId: string; reason: string },
) =>
  openedx.post<{ obligation_id: string; state: string; audit_action_id: string }>(
    `${PAYMENT_OPS_API}/obligations/${encodeURIComponent(obligationId)}/settle`,
    { action: 'write_off', operation_id: body.operationId, reason: body.reason },
    UI_HEADERS,
  );
